import { useEffect, useState } from 'react';
import FormElement from '../../../common/components/FormElement';
import { SiteContactDetailsWrapper } from './MeterUpgradeContactDetails.styles';
import { onlyNumbersInText } from '../../utils/text/text';
import { useSupportFormValidation } from '../../header/useSupportFormValidation';

type SiteContactDetailsProps = {
  siteData: Api.IMeterUpgradeSiteDetails;
  index: number;
  handleUpdateContactName: Function;
  handleUpdateContactNumber: Function;
  handleUpdateContactEmail: Function;
  handleUpdateParkingRestrictionRadio: Function;
  handleUpdateParkingRestrictionDetail: Function;
  handleUpdateAccessRestrictionRadio: Function;
  handleUpdateAccessRestrictionDetail: Function;
  handleUpdateAccessAboveRadio: Function;
  handleUpdateAccessAboveDetail: Function;
  handleUpdateSiteVulnerabilitiesRadio: Function;
  handleUpdateSiteVulnerabilitiesDetail: Function;
  submitButtonValidTouched: boolean | undefined;
};

export const SiteContactDetails = (props: SiteContactDetailsProps) => {
  const [siteMpans, setSiteMpans] = useState<Array<number>>([]);
  const [contactName, setContactName] = useState<string>('');
  const [contactNameTouched, setContactNameTouched] = useState<boolean>(false);

  const [contactNumber, setContactNumber] = useState<string>('');
  const [contactNumberTouched, setContactNumberTouched] = useState<boolean>(false);

  const [contactEmail, setContactEmail] = useState<string>('');
  const [contactEmailTouched, setContactEmailTouched] = useState<boolean>(false);

  const [parkingRestrictionRadio, setParkingRestrictionRadio] = useState<string>('No');
  const [parkingRestrictionDetail, setParkingRestrictionDetail] = useState<string>('');
  const [parkingRestrictionDetailTouched, setParkingRestrictionDetailTouched] = useState<boolean>(false);

  const [accessRestrictionRadio, setAccessRestrictionRadio] = useState<string>('No');
  const [accessRestrictionDetail, setAccessRestrictionDetail] = useState<string>('');
  const [accessRestrictionDetailTouched, setAccessRestrictionDetailTouched] = useState<boolean>(false);

  const [accessAboveRadio, setAccessAboveRadio] = useState<string>('No');
  const [accessAboveDetail, setAccessAboveDetail] = useState<string>('');
  const [accessAboveDetailTouched, setAccessAboveDetailTouched] = useState<boolean>(false);

  const [siteVulnerabilitiesRadio, setSiteVulnerabilitiesRadio] = useState<string>('No');
  const [siteVulnerabilitiesDetail, setSiteVulnerabilitiesDetail] = useState<string>('');
  const [siteVulnerabilitiesDetailTouched, setSiteVulnerabilitiesDetailTouched] = useState<boolean>(false);

  const placeHolder = 'Please add detail...';
  const lineNumber = props.index + 1;

  const {
    phoneError,
    messageError,
    emailError,
    parkingRestrictionDetailError,
    accessRestrictionDetailError,
    accessToMetersAbove8ftDetailError,
    vulnerabilitiesOnSiteDetailError,
    validateMessageInput,
    validateInput,
    validateParkingRestrictionDetail,
    validateAccessRestrictionDetail,
    validateAccessToMetersAbove8ftDetail,
    validateVulnerabilitiesOnSiteDetail,
    validateContactNumberInput,
    validateEmailInput,
    clearPhoneValidation,
    clearAllValidation,
  } = useSupportFormValidation();

  useEffect(() => {
    if (props.siteData.meters) {
      let tempMpanArray = [];
      for (let i = 0; i < props.siteData?.meters.length; i++) {
        tempMpanArray.push(props.siteData.meters[i].mpan);
      }
      setSiteMpans(tempMpanArray);
    }
  }, [props.siteData]);

  useEffect(() => {
    if (contactNameTouched) {
      validateInput(contactName);
    }
    if (contactNumberTouched) {
      validateContactNumberInput(contactNumber);
    }
    if (contactEmailTouched) {
      validateEmailInput(contactEmail);
    }

    if (parkingRestrictionDetailTouched || props.submitButtonValidTouched) {
      validateParkingRestrictionDetail(parkingRestrictionDetail);
    }

    if (accessRestrictionDetailTouched || props.submitButtonValidTouched) {
      validateAccessRestrictionDetail(accessRestrictionDetail);
    }

    if (accessAboveDetailTouched || props.submitButtonValidTouched) {
      validateAccessToMetersAbove8ftDetail(accessAboveDetail);
    }

    if (siteVulnerabilitiesDetailTouched || props.submitButtonValidTouched) {
      validateVulnerabilitiesOnSiteDetail(siteVulnerabilitiesDetail);
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
    // }, [message, phone, preference]);
  }, [
    contactName,
    contactNumber,
    contactEmail,
    parkingRestrictionDetail,
    accessRestrictionDetail,
    accessAboveDetail,
    siteVulnerabilitiesDetail,
    props.submitButtonValidTouched,
  ]);

  const handleUpdateContactName = (event: any) => {
    setContactNameTouched(true);
    setContactName(event.target.value);
    props.handleUpdateContactName(event.target.value, props.index);
  };

  const handleUpdateContactNumber = (event: any) => {
    if (onlyNumbersInText(event.target.value)) {
      setContactNumberTouched(true);
      setContactNumber(event.target.value);
      props.handleUpdateContactNumber(event.target.value, props.index);
    }
  };

  const handleUpdateContactEmail = (event: any) => {
    setContactEmail(event.target.value);
    setContactEmailTouched(true);
    props.handleUpdateContactEmail(event.target.value, props.index);
  };

  const handleUpdateParkingRestrictionRadio = (response: string) => {
    if (response === 'No') {
      setParkingRestrictionDetail('');
      props.handleUpdateParkingRestrictionDetail('', props.index);
    }

    setParkingRestrictionRadio(response);
    props.handleUpdateParkingRestrictionRadio(response, props.index);
  };

  const handleUpdateParkingRestrictionDetail = (event: any) => {
    setParkingRestrictionDetail(event.target.value);
    setParkingRestrictionDetailTouched(true);
    props.handleUpdateParkingRestrictionDetail(event.target.value, props.index);
  };

  const handleUpdateAccessRestrictionRadio = (response: string) => {
    if (response === 'No') {
      setAccessRestrictionDetail('');
      props.handleUpdateAccessRestrictionDetail('', props.index);
    }
    setAccessRestrictionRadio(response);
    props.handleUpdateAccessRestrictionRadio(response, props.index);
  };

  const handleUpdateAccessRestrictionDetail = (event: any) => {
    setAccessRestrictionDetail(event.target.value);
    setAccessRestrictionDetailTouched(true);
    props.handleUpdateAccessRestrictionDetail(event.target.value, props.index);
  };

  const handleUpdateAccessAboveRadio = (response: string) => {
    if (response === 'No') {
      setAccessAboveDetail('');
      props.handleUpdateAccessAboveDetail('', props.index);
    }
    setAccessAboveRadio(response);
    props.handleUpdateAccessAboveRadio(response, props.index);
  };

  const handleUpdateAccessAboveDetail = (event: any) => {
    setAccessAboveDetail(event.target.value);
    setAccessAboveDetailTouched(true);
    props.handleUpdateAccessAboveDetail(event.target.value, props.index);
  };

  const handleUpdateSiteVulnerabilitiesRadio = (response: string) => {
    if (response === 'No') {
      setSiteVulnerabilitiesDetail('');
      props.handleUpdateSiteVulnerabilitiesDetail('', props.index);
    }
    setSiteVulnerabilitiesRadio(response);
    props.handleUpdateSiteVulnerabilitiesRadio(response, props.index);
  };

  const handleUpdateSiteVulnerabilitiesDetail = (event: any) => {
    setSiteVulnerabilitiesDetail(event.target.value);
    setSiteVulnerabilitiesDetailTouched(true);
    props.handleUpdateSiteVulnerabilitiesDetail(event.target.value, props.index);
  };

  return (
    <>
      <SiteContactDetailsWrapper>
        <div className='formRowWrapper withInfo'>
          <div className='fieldWrapper'>
            {props.index === 0 && <h2 className='pb-2'>Site address</h2>}
            <div className={'pb-3'}>
              <div className='badge badge-pill badge-primary numberBadge mr-2'>{lineNumber}</div>

              {props.siteData?.siteAddress}
            </div>

            <div>
              <div>Contact name*</div>
              <div>
                <FormElement.Textarea
                  id='contactName'
                  placeholder={''}
                  className='no-resize form-element'
                  label={''}
                  name='contactName'
                  value={contactName}
                  onChange={e => handleUpdateContactName(e)}
                  onBlur={(e: React.FocusEvent<HTMLTextAreaElement>) => {
                    validateInput(e.target.value);
                  }}
                  errorMessage={messageError}
                  rows={1}
                />
              </div>

              <div>Contact number*</div>
              <div>
                <FormElement.Textarea
                  id='contactNumber'
                  placeholder={''}
                  className='no-resize form-element'
                  name='subject'
                  value={contactNumber}
                  onChange={e => handleUpdateContactNumber(e)}
                  onBlur={(e: React.FocusEvent<HTMLTextAreaElement>) => {
                    validateContactNumberInput(e.target.value);
                  }}
                  errorMessage={phoneError}
                  rows={1}
                  required={true}
                />
              </div>

              <div>Contact email*</div>
              <div>
                <FormElement.Textarea
                  id='contactEmail'
                  placeholder={''}
                  className='no-resize form-element'
                  name='contactEmail'
                  value={contactEmail}
                  onChange={e => handleUpdateContactEmail(e)}
                  onBlur={(e: React.FocusEvent<HTMLTextAreaElement>) => {
                    validateEmailInput(e.target.value);
                  }}
                  errorMessage={emailError}
                  rows={1}
                  required={true}
                />
              </div>
            </div>
          </div>
          <div className='infoPart'>
            {props.index === 0 && <h2 className='pb-2'>MPAN/s</h2>}
            <div>{siteMpans}</div>
          </div>
        </div>
        <div className='pb-3'>Please complete the following questions regarding the site</div>

        <div className='formRowWrapper withInfo'>
          <div className='fieldWrapper'>
            <div className='row'>
              <div className='col-6 pt-3'>Parking restrictions?*</div>
              <div className='col-6'>
                <FormElement.Radio
                  id='page-parking-restrictions'
                  className={''}
                  label={''}
                  name={''}
                  elements={['Yes', 'No']}
                  value={parkingRestrictionRadio}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                    handleUpdateParkingRestrictionRadio(e.target.value);
                  }}
                  required={true}
                />
              </div>
              {parkingRestrictionRadio === 'Yes' && (
                <div className='col-12'>
                  <FormElement.Textarea
                    id='specRef'
                    placeholder={placeHolder}
                    className='no-resize commentBox form-element'
                    label={''}
                    name='specRef'
                    value={parkingRestrictionDetail}
                    onChange={e => handleUpdateParkingRestrictionDetail(e)}
                    rows={1}
                    errorMessage={parkingRestrictionDetailError}
                    onBlur={(e: React.FocusEvent<HTMLTextAreaElement>) => {
                      validateParkingRestrictionDetail(e.target.value);
                    }}
                    required={true}
                  />
                </div>
              )}
            </div>

            <div className='row'>
              <div className='col-6 pt-3'>Access restrictions?*</div>
              <div className='col-6'>
                <FormElement.Radio
                  id='page-access-restrictions'
                  className={''}
                  label={''}
                  name={''}
                  elements={['Yes', 'No']}
                  value={accessRestrictionRadio}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                    handleUpdateAccessRestrictionRadio(e.target.value);
                  }}
                  required={true}
                />
              </div>
              {accessRestrictionRadio === 'Yes' && (
                <div className='col-12'>
                  <FormElement.Textarea
                    id='specRef'
                    placeholder={placeHolder}
                    className='no-resize commentBox form-element'
                    label={''}
                    name='specRef'
                    value={accessRestrictionDetail}
                    onChange={e => handleUpdateAccessRestrictionDetail(e)}
                    rows={1}
                    errorMessage={accessRestrictionDetailError}
                    onBlur={(e: React.FocusEvent<HTMLTextAreaElement>) => {
                      validateAccessRestrictionDetail(e.target.value);
                    }}
                    required={true}
                  />
                </div>
              )}
            </div>

            <div className='row'>
              <div className='col-6 pt-3'>Access to meters above 8ft?*</div>
              <div className='col-6'>
                <FormElement.Radio
                  id='page-access-above-restrictions'
                  className={''}
                  label={''}
                  name={''}
                  elements={['Yes', 'No']}
                  value={accessAboveRadio}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                    handleUpdateAccessAboveRadio(e.target.value);
                  }}
                  required={true}
                />
              </div>
              {accessAboveRadio === 'Yes' && (
                <div className='col-12'>
                  <FormElement.Textarea
                    id='specRef'
                    placeholder={placeHolder}
                    className='no-resize commentBox form-element'
                    label={''}
                    name='specRef'
                    value={accessAboveDetail}
                    onChange={e => handleUpdateAccessAboveDetail(e)}
                    rows={1}
                    errorMessage={accessToMetersAbove8ftDetailError}
                    onBlur={(e: React.FocusEvent<HTMLTextAreaElement>) => {
                      validateAccessToMetersAbove8ftDetail(e.target.value);
                    }}
                    required={true}
                  />
                </div>
              )}
            </div>

            <div className='row'>
              <div className='col-6 pt-3'>Are there any vulnerabilities at site?*</div>
              <div className='col-6'>
                <FormElement.Radio
                  id='page-vulnerabilities'
                  className={''}
                  label={''}
                  name={''}
                  elements={['Yes', 'No']}
                  value={siteVulnerabilitiesRadio}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                    handleUpdateSiteVulnerabilitiesRadio(e.target.value);
                  }}
                  required={true}
                />
              </div>
              {siteVulnerabilitiesRadio === 'Yes' && (
                <div className='col-12'>
                  <FormElement.Textarea
                    id='specRef'
                    placeholder={placeHolder}
                    className='no-resize commentBox form-element'
                    label={''}
                    name='specRef'
                    value={siteVulnerabilitiesDetail}
                    onChange={e => handleUpdateSiteVulnerabilitiesDetail(e)}
                    rows={1}
                    errorMessage={vulnerabilitiesOnSiteDetailError}
                    onBlur={(e: React.FocusEvent<HTMLTextAreaElement>) => {
                      validateVulnerabilitiesOnSiteDetail(e.target.value);
                    }}
                    required={true}
                  />
                </div>
              )}
            </div>
          </div>
        </div>
      </SiteContactDetailsWrapper>
    </>
  );
};
